import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { callMainAPI, callMainAPIAndLoadReports } from '../services/authService';

const SessionContext = createContext();

export const useSession = () => {
    const context = useContext(SessionContext);
    if (!context) {
        throw new Error('useSession must be used within a SessionProvider');
    }
    return context;
};

export const SessionProvider = ({ children }) => {
    const { isAuthenticated } = useAuth();
    const [sessionData, setSessionData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // Get session parameters using the storage split:
    //   userId       → localStorage.userIdentity  (cross-tab persistent)
    //   member params → sessionStorage.memberContext (tab-scoped, fresh per tab from URL)
    // Fallback to sessionStorage.user for backward compat with code written before the split.
    const getSessionParams = () => {
        try {
            // userId from localStorage.userIdentity
            let userId = '';
            try {
                const identity = JSON.parse(localStorage.getItem('userIdentity') || '{}');
                userId = identity.userId || '';
            } catch (_) {}

            // Member context from sessionStorage.memberContext (tab-scoped)
            let memberParams = { memberId: '', cid: '', caseId: '', systemId: '' };
            try {
                const ctx = JSON.parse(sessionStorage.getItem('memberContext') || '{}');
                memberParams.memberId = ctx.memberId || '';
                memberParams.cid      = ctx.cid      || '';
                memberParams.caseId   = ctx.caseId   || '';
                memberParams.systemId = ctx.systemId || '';
            } catch (_) {}

            // Fallback: sessionStorage.user (backward compat for both userId and member fields)
            if (!userId || (!memberParams.memberId && !memberParams.cid && !memberParams.caseId)) {
                try {
                    const u = JSON.parse(sessionStorage.getItem('user') || '{}');
                    if (!userId)                 userId            = u.userId || u.userID || u.UserID || '';
                    if (!memberParams.memberId)  memberParams.memberId = u.memberId  || '';
                    if (!memberParams.cid)       memberParams.cid      = u.cid       || '';
                    if (!memberParams.caseId)    memberParams.caseId   = u.caseId    || '';
                    if (!memberParams.systemId)  memberParams.systemId = u.systemId  || '';
                } catch (_) {}
            }

            return {
                UserID:   userId,
                MemberId: memberParams.memberId,
                SystemId: memberParams.systemId,
                CaseID:   memberParams.caseId,
                CID:      memberParams.cid
            };
        } catch (error) {
            return { UserID: '', MemberId: '', SystemId: '', CaseID: '', CID: '' };
        }
    };

    // Update URL with session parameters
    const updateURLWithParams = () => {
        const sessionParams = getSessionParams();
        const params = new URLSearchParams(window.location.search);
        Object.entries(sessionParams).forEach(([key, value]) => {
            params.set(key, value);
        });
        const newURL = `${window.location.pathname}?${params.toString()}`;
        window.history.replaceState({}, '', newURL);
    };

    // Call Main API after authentication
    useEffect(() => {
        const initializeSession = async () => {
            if (isAuthenticated && !sessionData) {
                setIsLoading(true);
                setError(null);
                
                try {
                    const urlParams = new URLSearchParams(window.location.search);
                    
                    // Use callMainAPIAndLoadReports to also load reports in the proper sequence
                    const response = await callMainAPIAndLoadReports(null);
                    
                    // Extract the main API response without the reports data
                    const mainResponseOnly = { ...response };
                    delete mainResponseOnly.reportsData;
                    
                    setSessionData(mainResponseOnly);
                    
                    // Don't update URL - keep the original parameters from the URL
                } catch (err) {
                    setError(err.message);
                } finally {
                    setIsLoading(false);
                }
            }
        };

        initializeSession();
    }, [isAuthenticated, sessionData]);

    // Resolve member params: Main API response is authoritative; fall back to session/storage params.
    const resolvedSessionParams = getSessionParams();
    const memberId  = sessionData?.memberId  || sessionData?.MemberId  || resolvedSessionParams.MemberId  || '';
    const cid       = sessionData?.cid       || sessionData?.CID       || resolvedSessionParams.CID       || '';
    const caseId    = sessionData?.caseId    || sessionData?.CaseID    || resolvedSessionParams.CaseID    || '';
    const systemId  = sessionData?.systemId  || sessionData?.SystemId  || resolvedSessionParams.SystemId  || '';
    const userId    = sessionData?.userId    || sessionData?.UserId    || resolvedSessionParams.UserID    || '';

    const value = {
        sessionData,
        isLoading,
        error,
        sessionParams: resolvedSessionParams,
        // Expose member context directly so consumers don't need to dig into sessionData
        userId,
        memberId,
        cid,
        caseId,
        systemId,
        menuItems: sessionData?.menuItems || [],
        isAdmin: sessionData?.isAdmin || false,
        userName: sessionData?.userName || sessionData?.UserName || '',
        hostName: sessionData?.hostName || sessionData?.HostName || '',
        adfsQsParam: sessionData?.adfsQsParam || sessionData?.AdfsQsParam || [],
        serviceConfiguration: sessionData?.serviceConfiguration || sessionData?.ServiceConfiguration || [],
        serviceParam: sessionData?.serviceParam || sessionData?.ServiceParam || [],
        refreshSession: async () => {
            setIsLoading(true);
            setError(null);
            try {
                // Use callMainAPIAndLoadReports to also load reports in the proper sequence
                const response = await callMainAPIAndLoadReports(null);
                
                // Extract the main API response without the reports data
                const mainResponseOnly = { ...response };
                delete mainResponseOnly.reportsData;
                
                setSessionData(mainResponseOnly);
                // Don't update URL - keep the original parameters
            } catch (err) {
                setError(err.message);
            } finally {
                setIsLoading(false);
            }
        }
    };

    return (
        <SessionContext.Provider value={value}>
            {children}
        </SessionContext.Provider>
    );
};
